/**
 * HighlightRefNodeView - メモに引用したハイライトの表示。出典をタップするとリーダーでその位置を開く
 */

import { Selection } from '@tiptap/pm/state'
import type { NodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import { BookOpen, Highlighter, Loader2, RotateCw, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import type { HighlightSource } from '@/api/services/highlight'
import { cn } from '@/lib/utils'

import {
  formatHighlightLocation,
  formatHighlightSource,
  highlightReaderPath,
  highlightRefAttrs,
  putHighlightRefs,
  requestHighlightRef,
  useHighlightRef,
} from './highlight-ref-cache'
import { highlightColorHex, HighlightPickerDialog } from './highlight-picker-dialog'

// 挿入直後のノードだけダイアログを開く (メモを開いたときに残っていた空ノードでは開かない)
let pickerRequested = false
export const requestHighlightPicker = () => {
  pickerRequested = true
}

export const HighlightRefNodeView = ({ node, editor, getPos, selected }: NodeViewProps) => {
  const highlightId = (node.attrs.highlightId ?? null) as number | null
  const snapshotText = String(node.attrs.text ?? '')
  const snapshotSource = String(node.attrs.source ?? '')
  const entry = useHighlightRef(highlightId)
  const canEdit = editor.isEditable
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    if (highlightId !== null || !pickerRequested) return
    pickerRequested = false
    setPickerOpen(true)
  }, [highlightId])

  // 未設定 (highlightId なし) の自ノード位置。非同期処理の後でも位置がずれないよう毎回引き直す
  const emptyPos = (): number | null => {
    const pos = getPos()
    if (typeof pos !== 'number') return null
    const current = editor.state.doc.nodeAt(pos)
    return current?.type.name === 'highlightRef' && current.attrs.highlightId == null ? pos : null
  }

  const removeSelf = (pos: number | null, opts: { focus: boolean }) => {
    if (pos === null) return
    const chain = opts.focus ? editor.chain().focus() : editor.chain()
    chain
      .command(({ tr }) => {
        // doc は block+ なので唯一のブロックなら段落に置き換える
        if (tr.doc.childCount === 1) {
          tr.replaceWith(pos, pos + 1, editor.schema.nodes.paragraph.create())
          tr.setSelection(Selection.near(tr.doc.resolve(pos + 1)))
        } else {
          tr.delete(pos, pos + 1)
          tr.setSelection(Selection.near(tr.doc.resolve(Math.min(pos, tr.doc.content.size)), -1))
        }
        return true
      })
      .run()
  }

  const linkHighlights = (picked: HighlightSource[]) => {
    const pos = emptyPos()
    if (pos === null || picked.length === 0) return
    putHighlightRefs(picked)
    const type = editor.schema.nodes.highlightRef
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.setNodeMarkup(pos, undefined, highlightRefAttrs(picked[0]))
        let after = pos + 1
        for (const h of picked.slice(1)) {
          const added = type.create(highlightRefAttrs(h))
          tr.insert(after, added)
          after += added.nodeSize
        }
        if (!tr.doc.resolve(after).nodeAfter?.isTextblock) {
          tr.insert(after, editor.schema.nodes.paragraph.create())
        }
        tr.setSelection(Selection.near(tr.doc.resolve(after + 1)))
        return true
      })
      .run()
  }

  if (highlightId === null) {
    if (!canEdit) return <NodeViewWrapper />
    return (
      <NodeViewWrapper className="my-0.5">
        <div
          contentEditable={false}
          className="flex items-center gap-1 rounded-md border border-dashed border-amber-500/40 bg-amber-500/8 px-1.5 py-1"
        >
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex min-w-0 flex-1 items-center gap-2 p-0! text-left text-muted-foreground hover:text-foreground"
          >
            <Highlighter className="size-4.5 shrink-0" />
            ハイライトを引用
          </button>
          <IconButton label="取り消す" onClick={() => removeSelf(emptyPos(), { focus: true })}>
            <X className="size-3.5" />
          </IconButton>
        </div>
        {pickerOpen && (
          <HighlightPickerDialog
            onPick={(picked) => {
              setPickerOpen(false)
              linkHighlights(picked)
            }}
            onClose={() => {
              setPickerOpen(false)
              removeSelf(emptyPos(), { focus: true })
            }}
          />
        )}
      </NodeViewWrapper>
    )
  }

  const highlight = typeof entry === 'object' ? entry : null

  return (
    <NodeViewWrapper className="my-1" data-drag-handle="">
      <div
        contentEditable={false}
        className={cn(
          'flex gap-2.5 rounded-md bg-muted/50 py-1.5 pr-1.5 pl-2',
          selected && 'outline-2 outline-(--tt-brand-color-500)',
        )}
      >
        <span
          className="w-1 shrink-0 self-stretch rounded-full"
          style={{ backgroundColor: highlight ? highlightColorHex(highlight.color) : 'var(--muted-foreground)' }}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="m-0! text-[0.95em] leading-relaxed whitespace-pre-wrap">
            {highlight?.text ?? snapshotText}
          </p>
          {highlight?.note && <p className="m-0! text-xs text-muted-foreground">✎ {highlight.note}</p>}
          <div className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
            {entry === undefined && <Loader2 className="size-3 shrink-0 animate-spin" />}
            {highlight ? <SourceLink highlight={highlight} /> : <span className="truncate">{snapshotSource}</span>}
            {entry === 'missing' && <span className="shrink-0">（削除されたハイライト）</span>}
            {entry === 'error' && (
              <IconButton label="再読み込み" onClick={() => requestHighlightRef(highlightId)}>
                <RotateCw className="size-3" />
              </IconButton>
            )}
          </div>
        </div>
        {entry === 'missing' && canEdit && (
          <IconButton label="メモから外す" onClick={() => removeSelf(getPos() ?? null, { focus: false })}>
            <X className="size-3.5" />
          </IconButton>
        )}
      </div>
    </NodeViewWrapper>
  )
}

function SourceLink({ highlight }: { highlight: HighlightSource }) {
  const location = formatHighlightLocation(highlight)
  return (
    <Link
      to={highlightReaderPath(highlight)}
      title="この位置を開く"
      className="flex min-w-0 items-center gap-1 rounded px-0.5 hover:bg-accent hover:text-foreground"
    >
      <BookOpen className="size-3 shrink-0" />
      <span className="truncate">{formatHighlightSource(highlight)}</span>
      {location && <span className="shrink-0">· {location}</span>}
    </Link>
  )
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="shrink-0 self-start rounded p-0.5! text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      {children}
    </button>
  )
}
